import { Box } from '@mui/material'
import React, { useEffect, FC } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { appUpdate } from '@e3dc-react/shell/State/App/AppActions'
import { makeStyles } from 'tss-react/mui'
import { setActiveInstallation, setActiveStorage } from 'src/State/Storage/StorageActions'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

import Information from './Information'
import Systems from './Systems'
import PaperContainer from './PaperContainer'
import { useDetailedInstallationData } from '../hooks/use-detailed-installation-data'
import InstallationDecommissionedBanner from './decomission-banner/InstallationDecommissionedBanner'
import { usePageActionsContext } from 'src/Libs/header/context'
import AdditionalDevices from './AdditionalDevices'
import InstallationHistory from './InstallationHistory'
import { useInstallationHistory } from 'src/Libs/installations/hooks/use-installation-history'
import { useInstallationAccessType } from 'src/Libs/installations/hooks/use-installation-access-type'
import { Installation as InstallationModel } from 'src/Libs/installations'
import { isFlowTheme } from 'src/Libs/utils'

const useStyles = makeStyles()(theme => ({
  container: {
    gap: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
  },
}))

/**
 * This component represents a page for details of the selected installation
 * @returns Installation Component
 **/
const Installation: FC = () => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { dispatchPageActions } = usePageActionsContext()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { installationId } = useParams<{ installationId: string }>()
  const [installation, installationLoading, refetchInstallationData] = useDetailedInstallationData(installationId)
  const [{ data: installationHistoryData }, refetchInstallationHistoryData] = useInstallationHistory(installationId)
  const installationAccessType = useInstallationAccessType(installation as InstallationModel)
  const isPlannedInstallation = installationAccessType === 'e3dc-setup-configurator-access'

  const refetchInstallation = (): void => {
    refetchInstallationData()
    refetchInstallationHistoryData()
  }

  useEffect(() => {
    if (isPlannedInstallation) {
      navigate(`/installation/${installationId}/configuration/general`, { replace: true })
    }
  }, [isPlannedInstallation, navigate, installationId])

  useEffect(() => {
    dispatchPageActions?.([{ position: 'left', type: 'back', onClick: () => navigate(-1) }])

    return () => {
      dispatchPageActions?.([])
    }
  }, [dispatchPageActions, navigate])

  useEffect(() => {
    dispatch(appUpdate({ title: t('installation-details.title', { id: installationId }) }))
  }, [installationId]) //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const installationIdToUse = installationId ? Number.parseInt(installationId) : undefined
    dispatch(setActiveInstallation(installationIdToUse))
    if (installation && !installationLoading) {
      dispatch(setActiveStorage(installation?.emsMasterDeviceId))
    }
  }, [dispatch, installationId, installation, installationLoading])

  return (
    <Box className={classes.container}>
      <InstallationDecommissionedBanner installation={installation} />
      <Information
        installationId={installationId}
        installation={installation}
        refetchInstallation={refetchInstallation}
        installationLoading={installationLoading}
      />
      {!isFlowTheme && (
        <PaperContainer title={t('installation.details.systems.title')}>
          <Systems installation={installation ?? undefined} />
        </PaperContainer>
      )}
      {!isFlowTheme && (
        <PaperContainer title={t('installation.details.additional-devices.title')}>
          <AdditionalDevices installationId={installationId} />
        </PaperContainer>
      )}
      {!isFlowTheme && (
        <PaperContainer title={t('installation.details.installation-history.title')}>
          <InstallationHistory installationHistoryData={installationHistoryData ?? []} />
        </PaperContainer>
      )}
    </Box>
  )
}

export default Installation
